import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

export default function AdminFareEditor({ apiBase = "https://gantasoft.in/cab-g/dev/admin" }) {
  const api = useMemo(() => axios.create({ baseURL: apiBase }), [apiBase]);

  const [cities, setCities] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [cityId, setCityId] = useState("");
  const [rows, setRows] = useState([]); // fares for selected city
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      const cs = await api.get("/cities_list.php"); setCities(cs.data.cities);
      const vs = await api.get("/vehicle_types.php"); setVehicles(vs.data.vehicle_types);
    })();
  }, [api]);

  const loadFares = async (cid) => {
    if (!cid) return setRows([]);
    const r = await api.get("/fares_get.php", { params: { city_id: cid } });
    // ensure every vehicle shows a row (even if not yet created)
    const byCode = Object.fromEntries(r.data.fares.map(f => [f.vehicle_code, f]));
    const full = vehicles.map(v => byCode[v.code] || {
      id: null, city_id: +cid, vehicle_type_id: v.id, vehicle_code: v.code, display_name: v.display_name,
      min_fare_amount: 0, min_fare_km: 1.6, per_km: 0, per_min: 0, waiting_per_min: 0,
      night_multiplier: 1, night_start: "22:00:00", night_end: "05:00:00",
      apply_night_automatically: 1, include_fixed_fee: 0, active: 1
    });
    setRows(full);
  };

  useEffect(() => { if (cityId) loadFares(cityId); }, [cityId, vehicles]);

  const upd = (idx, key, val) => {
    setRows(rs => rs.map((r,i)=> i===idx ? { ...r, [key]: val } : r));
  };

  const save = async () => {
    setSaving(true);
    try {
      for (const r of rows) {
        await api.post("/fare_upsert.php", {
          city_id: r.city_id,
          vehicle_type_id: r.vehicle_type_id,
          min_fare_amount: +r.min_fare_amount,
          min_fare_km: +r.min_fare_km,
          per_km: +r.per_km,
          per_min: +r.per_min,
          waiting_per_min: +r.waiting_per_min,
          night_multiplier: +r.night_multiplier,
          night_start: r.night_start,
          night_end: r.night_end,
          include_fixed_fee: +r.include_fixed_fee,
          apply_night_automatically: r.apply_night_automatically ? 1 : 0,
          active: r.active ? 1 : 0
        });
      }
      alert("Saved!");
    } catch (e) {
      alert("Save failed: " + (e?.response?.data?.message || e.message));
    } finally {
      setSaving(false);
      if (cityId) loadFares(cityId);
    }
  };

  return (
    <div className="p-4 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold mb-4">Fare Rules</h1>

      <div className="flex gap-3 items-center mb-4">
        <label className="font-medium">City</label>
        <select className="border rounded px-3 py-2"
                value={cityId} onChange={e=>setCityId(e.target.value)}>
          <option value="">-- Select --</option>
          {cities.map(c=> <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button onClick={()=>cityId && loadFares(cityId)}
                className="px-3 py-2 border rounded">Reload</button>
      </div>

      {!cityId ? <p>Select a city.</p> : (
        <div className="overflow-x-auto border rounded">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="p-2 text-left">Vehicle</th>
                <th className="p-2">Min Fare (₹)</th>
                <th className="p-2">Min KM</th>
                <th className="p-2">Per KM (₹)</th>
                <th className="p-2">Per Min (₹)</th>
                <th className="p-2">Waiting / Min (₹)</th>
                <th className="p-2">Night x</th>
                <th className="p-2">Night Start</th>
                <th className="p-2">Night End</th>
                <th className="p-2">Fixed Fee (₹)</th>
                <th className="p-2">Auto Night</th>
                <th className="p-2">Active</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r,idx)=> (
                <tr key={r.vehicle_code} className="border-t">
                  <td className="p-2 font-medium">{r.display_name}</td>
                  <td className="p-1"><input className="w-24 border rounded px-2 py-1" value={r.min_fare_amount}
                    onChange={e=>upd(idx,'min_fare_amount',e.target.value)} /></td>
                  <td className="p-1"><input className="w-20 border rounded px-2 py-1" value={r.min_fare_km}
                    onChange={e=>upd(idx,'min_fare_km',e.target.value)} /></td>
                  <td className="p-1"><input className="w-20 border rounded px-2 py-1" value={r.per_km}
                    onChange={e=>upd(idx,'per_km',e.target.value)} /></td>
                  <td className="p-1"><input className="w-20 border rounded px-2 py-1" value={r.per_min}
                    onChange={e=>upd(idx,'per_min',e.target.value)} /></td>
                  <td className="p-1"><input className="w-24 border rounded px-2 py-1" value={r.waiting_per_min}
                    onChange={e=>upd(idx,'waiting_per_min',e.target.value)} /></td>
                  <td className="p-1"><input className="w-16 border rounded px-2 py-1" value={r.night_multiplier}
                    onChange={e=>upd(idx,'night_multiplier',e.target.value)} /></td>
                  <td className="p-1"><input type="time" className="border rounded px-2 py-1" value={r.night_start}
                    onChange={e=>upd(idx,'night_start',e.target.value)} /></td>
                  <td className="p-1"><input type="time" className="border rounded px-2 py-1" value={r.night_end}
                    onChange={e=>upd(idx,'night_end',e.target.value)} /></td>
                  <td className="p-1"><input className="w-20 border rounded px-2 py-1" value={r.include_fixed_fee}
                    onChange={e=>upd(idx,'include_fixed_fee',e.target.value)} /></td>
                  <td className="p-1">
                    <input type="checkbox" checked={!!r.apply_night_automatically}
                           onChange={e=>upd(idx,'apply_night_automatically', e.target.checked?1:0)} />
                  </td>
                  <td className="p-1">
                    <input type="checkbox" checked={!!r.active}
                           onChange={e=>upd(idx,'active', e.target.checked?1:0)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4">
        <button onClick={save} disabled={saving}
                className="px-4 py-2 rounded bg-green-600 text-white hover:bg-green-700 disabled:opacity-50">
          {saving ? "Saving..." : "Save All"}
        </button>
      </div>
    </div>
  );
}
